import { useLayoutEffect, useState, type RefObject } from 'react';

// Priority+ overflow for a one-line row of tabs (lifted from MainNav): how many
// leading items fit in the row; the rest go into a "More" menu. Widths come
// from a hidden measuring row inside `measureRef` that renders EVERY item at
// full size ([data-mi]) plus a "More" sentinel ([data-mi-more]), so the answer
// does not depend on what is currently shown. `reserve` is room taken in the
// same row by siblings (a record label after the tabs). Without ResizeObserver
// (jsdom, very old engines) it measures once; a row with no layout (0px wide)
// shows every item.
// `activeIndex` pins one item (the active tab): its width is reserved first, so
// activating or focusing a tab never pushes it into More. The return value is
// then the number of LEADING items shown; the pinned one is shown as well even
// when its index is at or past that number (see `isShown`).
export function isShown(index: number, visible: number, activeIndex?: number): boolean {
  return index < visible || index === activeIndex;
}

export function usePriorityOverflow({ containerRef, measureRef, count, gap, enabled = true, reserve, activeIndex, deps = [] }: {
  containerRef: RefObject<HTMLElement | null>;
  measureRef: RefObject<HTMLElement | null>;
  count: number;
  gap: number;
  enabled?: boolean;
  reserve?: () => number;
  activeIndex?: number;
  deps?: ReadonlyArray<unknown>;
}): number {
  const [visible, setVisible] = useState(count);
  useLayoutEffect(() => {
    if (!enabled) { setVisible(count); return; }
    const box = containerRef.current;
    const measurer = measureRef.current;
    if (!box || !measurer) { setVisible(count); return; }
    const recompute = () => {
      // Not laid out (jsdom, or a display:none ancestor): show everything.
      if (box.clientWidth <= 0) { setVisible(count); return; }
      const items = Array.from(measurer.querySelectorAll<HTMLElement>('[data-mi]'));
      if (!items.length) { setVisible(count); return; }
      const moreW = measurer.querySelector<HTMLElement>('[data-mi-more]')?.offsetWidth ?? 0;
      const avail = box.clientWidth - (reserve ? reserve() : 0);
      const w = items.map((el) => el.offsetWidth);
      const full = w.reduce((a, b) => a + b, 0) + gap * (w.length - 1);
      if (full <= avail) { setVisible(count); return; }
      const fitWith = (pinned: number) => {
        let used = 0;
        let fit = 0;
        for (let i = 0; i < w.length; i++) {
          const next = used + (fit > 0 ? gap : 0) + w[i];
          if (next + gap + moreW + pinned <= avail) { used = next; fit++; } else break;
        }
        return fit;
      };
      const fit = fitWith(0);
      if (activeIndex === undefined || activeIndex < 0 || activeIndex >= w.length || activeIndex < fit) { setVisible(fit); return; }
      // The active item would be cut: keep it and fit the leading items around it.
      setVisible(fitWith(gap + w[activeIndex]));
    };
    recompute();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(recompute);
    ro.observe(box);
    ro.observe(measurer);
    return () => ro.disconnect();
  }, [enabled, count, gap, activeIndex, ...deps]); // eslint-disable-line react-hooks/exhaustive-deps
  return visible;
}
